import {
  CORRECTIVE_ACTION_STATUS_LABELS,
  type CorrectiveAction,
  INSPECTION_AUTHORITY_LABELS,
  INSPECTION_RESULT_LABELS,
  INSPECTION_STATUS_LABELS,
  type InspectionDetail,
} from "@station/contracts";
import { Alert, Button, Card, EmptyState, MoneyText, SkeletonRows, StatusBadge, useToast } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, SearchX } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionDialog,
  CompleteActionDialog,
  CompleteInspectionDialog,
  ReasonDialog,
} from "../../../../features/compliance/inspection-dialogs.js";
import { useInspection, useRefreshCompliance } from "../../../../features/compliance/queries.js";
import { useMaintenancePermissions } from "../../../../features/maintenance/queries.js";
import { DetailRow, FileOpenButton, MaintenancePage } from "../../../../features/maintenance/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage, isApiError } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/uyum/denetimler/$id")({ component: InspectionRoute });

type DialogState =
  | { kind: "complete" }
  | { kind: "cancel" }
  | { kind: "addAction" }
  | { kind: "editAction"; action: CorrectiveAction }
  | { kind: "completeAction"; action: CorrectiveAction }
  | { kind: "cancelAction"; action: CorrectiveAction };

/**
 * Denetim ziyareti (Prompt 23 web, `GET /compliance/inspections/:id`): kurum, tarih, durum, sonuç, ceza, tutanak, not ve
 * düzeltici faaliyetler (sorumlu, son tarih, gecikme, kanıt). Alarm bağlantısı (`CORRECTIVE_ACTION_DUE`) bu ekrana gelir.
 */
function InspectionRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const toast = useToast();
  const refresh = useRefreshCompliance();
  const permissions = useMaintenancePermissions();
  const inspection = useInspection(id, permissions.enabled && permissions.canReadCompliance);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const data = inspection.data;
  const title = data
    ? `${data.authorityName ?? INSPECTION_AUTHORITY_LABELS[data.authority]} · ${formatDate(data.visitDate)}`
    : t("compliance.inspections.detailTitle");
  return (
    <MaintenancePage
      title={title}
      {...(data ? { description: `${data.stationCode} ${data.stationName}` } : {})}
      requirement="compliance"
      breadcrumb={[
        {
          label: t("menu.complianceInspections"),
          link: (children, className) => (
            <Link to="/uyum/denetimler" className={className}>
              {children}
            </Link>
          ),
        },
        { label: title },
      ]}
      actions={
        data?.canManage ? (
          <div className="flex flex-wrap gap-2">
            {data.status === "PLANNED" ? (
              <>
                <Button onClick={() => setDialog({ kind: "complete" })}>
                  {t("compliance.inspections.complete")}
                </Button>
                <Button variant="ghost" onClick={() => setDialog({ kind: "cancel" })}>
                  {t("compliance.inspections.cancel")}
                </Button>
              </>
            ) : null}
            {data.status === "COMPLETED" ? (
              <Button onClick={() => setDialog({ kind: "addAction" })}>
                <Plus aria-hidden="true" />
                {t("compliance.actions.add")}
              </Button>
            ) : null}
          </div>
        ) : null
      }
    >
      {inspection.error ? (
        isApiError(inspection.error, "NOT_FOUND") ? (
          <EmptyState
            headingLevel={2}
            icon={<SearchX />}
            title={t("compliance.inspections.notFoundTitle")}
            description={t("compliance.inspections.notFoundDescription")}
          />
        ) : (
          <Alert tone="danger">{errorMessage(inspection.error)}</Alert>
        )
      ) : !data ? (
        <SkeletonRows rows={6} />
      ) : (
        <InspectionBody inspection={data} onDialog={setDialog} />
      )}
      {data && dialog?.kind === "complete" ? (
        <CompleteInspectionDialog open onOpenChange={() => setDialog(null)} inspection={data} />
      ) : null}
      {data && dialog?.kind === "cancel" ? (
        <ReasonDialog
          open
          onOpenChange={() => setDialog(null)}
          title={t("compliance.inspections.cancel")}
          description={t("compliance.inspections.cancelDescription")}
          submitLabel={t("compliance.inspections.cancel")}
          onSubmit={async (reason) => {
            await api.POST("/compliance/inspections/{id}/cancel", {
              params: { path: { id: data.id } },
              body: { reason, version: data.version },
            });
            await refresh();
            toast({ tone: "success", title: t("compliance.inspections.cancelled") });
          }}
        />
      ) : null}
      {data && dialog?.kind === "addAction" ? (
        <ActionDialog open onOpenChange={() => setDialog(null)} inspection={data} action={null} />
      ) : null}
      {data && dialog?.kind === "editAction" ? (
        <ActionDialog open onOpenChange={() => setDialog(null)} inspection={data} action={dialog.action} />
      ) : null}
      {data && dialog?.kind === "completeAction" ? (
        <CompleteActionDialog
          open
          onOpenChange={() => setDialog(null)}
          inspection={data}
          action={dialog.action}
        />
      ) : null}
      {data && dialog?.kind === "cancelAction" ? (
        <ReasonDialog
          open
          onOpenChange={() => setDialog(null)}
          title={t("compliance.actions.cancel")}
          description={dialog.action.description}
          submitLabel={t("compliance.actions.cancel")}
          onSubmit={async (reason) => {
            await api.POST("/compliance/inspections/{id}/actions/{actionId}/cancel", {
              params: { path: { id: data.id, actionId: dialog.action.id } },
              body: { reason, version: dialog.action.version },
            });
            await refresh();
            toast({ tone: "success", title: t("compliance.actions.cancelled") });
          }}
        />
      ) : null}
    </MaintenancePage>
  );
}

function InspectionBody({
  inspection,
  onDialog,
}: {
  inspection: InspectionDetail;
  onDialog: (state: DialogState) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <Card className="grid gap-4 p-4">
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailRow label={t("compliance.fields.inspectionStatus")}>
            {INSPECTION_STATUS_LABELS[inspection.status]}
          </DetailRow>
          <DetailRow label={t("compliance.fields.result")}>
            {inspection.result ? INSPECTION_RESULT_LABELS[inspection.result] : "—"}
          </DetailRow>
          <DetailRow label={t("compliance.fields.penaltyAmount")}>
            <MoneyText value={inspection.penaltyAmount} />
          </DetailRow>
          <DetailRow label={t("compliance.fields.createdAt")}>
            {formatDateTime(inspection.createdAt)}
          </DetailRow>
        </dl>
        {inspection.notes ? <p className="whitespace-pre-line text-sm">{inspection.notes}</p> : null}
        {inspection.reportFileId ? (
          <FileOpenButton fileId={inspection.reportFileId} label={t("compliance.inspections.openReport")} />
        ) : null}
      </Card>
      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("compliance.fields.correctiveActions")}</h2>
        {inspection.correctiveActions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("compliance.actions.none")}</p>
        ) : (
          <ul className="grid gap-2">
            {inspection.correctiveActions.map((action) => (
              <li key={action.id} className="grid gap-2 rounded-md border p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <span className="grid gap-0.5">
                    <span className="font-medium">{action.description}</span>
                    <span className="text-xs text-muted-foreground">
                      {t("compliance.fields.dueDate")} {formatDate(action.dueDate)}
                      {action.responsibleUser ? ` · ${action.responsibleUser.name}` : ""}
                    </span>
                  </span>
                  <span className="flex flex-wrap gap-1.5">
                    <StatusBadge
                      tone={
                        action.status === "DONE"
                          ? "success"
                          : action.status === "CANCELLED"
                            ? "neutral"
                            : "info"
                      }
                    >
                      {CORRECTIVE_ACTION_STATUS_LABELS[action.status]}
                    </StatusBadge>
                    {action.dueState === "OVERDUE" ? (
                      <StatusBadge tone="danger">{t("compliance.actions.overdue")}</StatusBadge>
                    ) : null}
                    {action.dueState === "DUE_SOON" ? (
                      <StatusBadge tone="warning">{t("compliance.actions.dueSoon")}</StatusBadge>
                    ) : null}
                  </span>
                </div>
                {action.completionNote ? (
                  <p className="text-sm text-muted-foreground">
                    {action.completionNote}
                    {action.completedAt ? ` · ${formatDateTime(action.completedAt)}` : ""}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {action.evidenceFileId ? (
                    <FileOpenButton
                      fileId={action.evidenceFileId}
                      label={t("compliance.actions.openEvidence")}
                    />
                  ) : null}
                  {inspection.canManage && action.status === "OPEN" ? (
                    <>
                      <Button size="sm" onClick={() => onDialog({ kind: "completeAction", action })}>
                        {t("compliance.actions.complete")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDialog({ kind: "editAction", action })}
                      >
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDialog({ kind: "cancelAction", action })}
                      >
                        {t("compliance.actions.cancel")}
                      </Button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
